import { DSDivider, DSFlex, DSSpacer, DSText } from '@zoe/ds-web'
import React, { useEffect, useState } from 'react'

import { LOCALE_GB } from '@/types/locales'

import { useLocale } from '@/context/localeContext'

import { DSGrid, DSGridBlock } from '@/components/ds/Grid/Grid'
import { DSHide } from '@/components/ds/Responsive/Hide/Hide'
import { Copyright } from '@/components/elements/footer/Copyright'
import { FooterLocalePicker } from '@/components/elements/footer/FooterLocalePicker'
import { FooterNewsletterSignup } from '@/components/elements/footer/FooterNewsletterSignup'

import { trackCtaClick } from '@/lib/gtm/gtmV2'

import { FooterCards } from './FooterCards'
import { FooterCompanyMenu } from './FooterCompanyMenu'
import { FooterLegalMenu } from './FooterLegalMenu'
import { FooterResourcesMenu } from './FooterResourcesMenu'
import { FooterSocialMenu } from './FooterSocialMenu'

type FooterProps = {
  showPreFooter?: boolean
}

export const trackFooterClick = (label: string) => {
  trackCtaClick('footer', label, { link_position: 'footer' })
}

export const Footer: React.FC<FooterProps> = ({ showPreFooter }) => {
  const { currentLocale } = useLocale()
  const [showCompanyAddress, setShowCompanyAddress] = useState(false)

  useEffect(() => {
    if (currentLocale === LOCALE_GB) {
      setShowCompanyAddress(true)
    }
  }, [currentLocale])

  return (
    <>
      {showPreFooter && (
        <DSGrid backgroundColor="aubergine-bold" mobileBackgroundColor="aubergine-bold" backgroundFullWidth>
          <DSGridBlock breakConfig={{ m: { order: 1 }, s: { order: 1 } }}>
            <DSSpacer size={144} direction="vertical" breakAt={{ m: 96 }} />
            <DSText as="h2" variant="fluid-label-100" color="primary-inverse">
              EXPLORE ZOE
            </DSText>
            <DSSpacer size={32} direction="vertical" />
            <DSHide below="m">
              <DSDivider variant="secondary" />
              <DSSpacer size={48} direction="vertical" />
            </DSHide>
          </DSGridBlock>

          <DSGridBlock
            breakConfig={{
              xl: { colSpan: 3 },
              l: { colSpan: 3 },
              m: { colSpan: 3, order: 3 },
              s: { colSpan: 2, order: 3 },
            }}
          >
            <DSSpacer size={0} breakAt={{ l: 72 }} direction="vertical" />
            <FooterNewsletterSignup />
          </DSGridBlock>
          <DSGridBlock
            breakConfig={{
              xl: { colSpan: 8, offset: 1 },
              l: { colSpan: 8, offset: 1 },
              m: { colSpan: 6, order: 2 },
              s: { colSpan: 2, order: 2 },
            }}
          >
            <FooterCards />
          </DSGridBlock>
          <DSGridBlock breakConfig={{ m: { order: 4 }, s: { order: 4 } }}>
            <DSHide below="m">
              <DSSpacer size={72} direction="vertical" />
            </DSHide>
            <DSDivider variant="secondary" />
            <DSSpacer size={48} direction="vertical" breakAt={{ m: 96 }} />
          </DSGridBlock>
        </DSGrid>
      )}
      <DSGrid backgroundColor="aubergine-bold" mobileBackgroundColor="aubergine-bold" backgroundFullWidth>
        {!showPreFooter && <DSSpacer size={144} direction="vertical" breakAt={{ m: 96 }} />}
        <DSGridBlock breakConfig={{ xl: { colSpan: 2 }, l: { colSpan: 2 }, m: { colSpan: 6 } }}>
          <FooterSocialMenu />
          <DSSpacer size={0} direction="vertical" breakAt={{ l: 48 }} />
        </DSGridBlock>

        <DSGridBlock
          breakConfig={{
            xl: { colSpan: 2, offset: 2 },
            l: { colSpan: 2, offset: 2 },
            m: { colSpan: 3 },
            s: { colSpan: 2 },
          }}
        >
          <FooterCompanyMenu />
          {showCompanyAddress && (
            <>
              <DSSpacer size={16} direction="vertical" />
              <DSText as="p" variant="fluid-label-100" color="primary-inverse" weight="semiBold">
                Location
              </DSText>
              <DSSpacer size={16} direction="vertical" />
              <DSText as="p" variant="fluid-label-100" color="primary-inverse" weight="light">
                483 Green Lanes
              </DSText>
              <DSText as="p" variant="fluid-label-100" color="primary-inverse" weight="light">
                London, N13 4BS
              </DSText>
            </>
          )}

          <DSSpacer size={0} direction="vertical" breakAt={{ m: 32 }} />
        </DSGridBlock>
        <DSGridBlock breakConfig={{ xl: { colSpan: 3 }, l: { colSpan: 3 }, m: { colSpan: 3 }, s: { colSpan: 2 } }}>
          <FooterLegalMenu />
          <DSSpacer size={0} direction="vertical" breakAt={{ m: 32 }} />
        </DSGridBlock>

        <DSGridBlock breakConfig={{ xl: { colSpan: 2 }, l: { colSpan: 2 }, m: { colSpan: 3 }, s: { colSpan: 2 } }}>
          <FooterResourcesMenu />
        </DSGridBlock>
        <DSSpacer size={48} direction="vertical" />
        <DSDivider variant="secondary" />
        <DSSpacer size={16} direction="vertical" />
        <DSFlex direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Copyright />
          <FooterLocalePicker />
        </DSFlex>
        <DSSpacer size={144} direction="vertical" breakAt={{ m: 96 }} />
      </DSGrid>
    </>
  )
}
